import { STATES_BY_SLUG } from "@/lib/generated/states";
import { resolveProductUrlSlug } from "@/lib/products/catalog/resolution";
import { collapseConditionSeparators } from "@shared/products/condition-filter";
import {
  LISTING_INTENT_OPTIONS,
  type ListingIntentFilter,
  type PriceRange,
} from "@/components/listing/browse/context/listing-browse-types";

type RawParams = Record<string, string | string[] | undefined>;

export interface ListingSearchLocation {
  lat: number;
  lng: number;
  postalCode?: string;
  city?: string;
  state?: string;
}

export interface ListingSearchState {
  location?: ListingSearchLocation;
  radiusMiles?: number;
  quantity?: number;
  productType?: string;
  searchQuery?: string;
  intentFilter?: ListingIntentFilter;
  conditions?: string[];
  priceRange?: PriceRange;
  stateCode?: string;
  listedAfterDays?: number;
}

function parseConditions(raw: string | undefined): string[] | undefined {
  if (!raw) return undefined;
  return raw
    .split(",")
    .map((entry) => collapseConditionSeparators(entry.trim()))
    .filter(Boolean);
}

function readParam(params: RawParams, key: string): string | undefined {
  const value = params[key];
  const raw = Array.isArray(value) ? value.at(-1) : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : undefined;
}

function readNumber(params: RawParams, key: string): number | undefined {
  const raw = readParam(params, key);
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readWholeNumber(
  params: RawParams,
  key: string,
  round: (value: number) => number,
): number | undefined {
  const raw = readNumber(params, key);
  if (raw === undefined) return undefined;
  const whole = round(raw);
  return whole >= 1 ? whole : undefined;
}

function parseLocation(params: RawParams): ListingSearchLocation | undefined {
  const lat = readNumber(params, "lat");
  const lng = readNumber(params, "lng");
  if (lat === undefined || lng === undefined) return undefined;
  return {
    lat,
    lng,
    postalCode: readParam(params, "zip"),
    city: readParam(params, "city"),
    state: readParam(params, "state"),
  };
}

function parseIntent(params: RawParams): ListingIntentFilter | undefined {
  const raw = readParam(params, "intent");
  return LISTING_INTENT_OPTIONS.find((option) => option.value === raw)?.value;
}

function parsePriceRange(params: RawParams): PriceRange | undefined {
  const min = readNumber(params, "priceMin");
  const max = readNumber(params, "priceMax");
  if (min === undefined && max === undefined) return undefined;
  return { min: min ?? null, max: max ?? null };
}

export function isUnfilteredListingSearch({
  quantity,
  radiusMiles,
  intentFilter,
  ...filters
}: ListingSearchState): boolean {
  return (
    (quantity ?? 1) <= 1 &&
    (intentFilter ?? "all") === "all" &&
    Object.values(filters).every((value) => value === undefined)
  );
}

export function parseListingSearchParams(
  params: RawParams,
): ListingSearchState {
  const product = readParam(params, "product");
  const productType = product
    ? (resolveProductUrlSlug(product) ?? product)
    : undefined;
  const conditions = parseConditions(readParam(params, "condition"));
  const quantity = readWholeNumber(params, "qty", Math.round);
  const radiusMiles = readNumber(params, "radius");
  const listedAfterDays = readWholeNumber(params, "listed", Math.floor);
  const location = parseLocation(params);
  const legacyState = location
    ? undefined
    : readParam(params, "state")?.toLowerCase();
  const stateCode =
    Object.values(STATES_BY_SLUG).find((state) =>
      state.code === readParam(params, "st")?.toUpperCase(),
    )?.code ??
    Object.values(STATES_BY_SLUG).find((state) =>
      [state.code.toLowerCase(), state.slug, state.name.toLowerCase()].includes(
        legacyState ?? "",
      ),
    )?.code;
  const intentFilter = parseIntent(params);

  return {
    stateCode,
    listedAfterDays,
    location,
    radiusMiles:
      radiusMiles !== undefined && radiusMiles >= 0 ? radiusMiles : undefined,
    quantity,
    productType,
    searchQuery: readParam(params, "q"),
    intentFilter,
    conditions: conditions?.length ? conditions : undefined,
    priceRange:
      intentFilter === "recycle" ? undefined : parsePriceRange(params),
  };
}

function appendLocation(
  search: URLSearchParams,
  location: ListingSearchLocation | undefined,
  radiusMiles: number | undefined,
) {
  if (!location) return;
  if (location.postalCode) search.set("zip", location.postalCode);
  search.set("lat", String(location.lat));
  search.set("lng", String(location.lng));
  if (location.city) search.set("city", location.city);
  if (location.state) search.set("state", location.state);
  if (radiusMiles !== undefined) search.set("radius", String(radiusMiles));
}

export function buildListingSearchQuery(state: ListingSearchState): string {
  const search = new URLSearchParams();
  appendLocation(search, state.location, state.radiusMiles);
  const hasQuantity =
    state.quantity !== undefined &&
    Number.isFinite(state.quantity) &&
    state.quantity > 1;
  if (hasQuantity) {
    search.set("qty", String(state.quantity));
  }
  if (state.productType) {
    search.set("product", state.productType);
  }
  if (state.searchQuery) search.set("q", state.searchQuery);
  if (state.intentFilter && state.intentFilter !== "all") {
    search.set("intent", state.intentFilter);
  }
  if (state.conditions?.length) {
    search.set("condition", state.conditions.join(","));
  }
  if (state.priceRange?.min !== null && state.priceRange?.min !== undefined) {
    search.set("priceMin", String(state.priceRange.min));
  }
  if (state.priceRange?.max !== null && state.priceRange?.max !== undefined) {
    search.set("priceMax", String(state.priceRange.max));
  }
  if (state.stateCode) search.set("st", state.stateCode);
  if (state.listedAfterDays !== undefined) {
    search.set("listed", String(state.listedAfterDays));
  }
  return search.toString();
}
